import { debounce } from '@theme/utilities';
import { morph } from '@theme/morph';
import { sectionRenderer } from '@theme/section-renderer';

function initHeaderPredictive(form) {
  const input = form.querySelector('.header-search-form__input');
  if (!input) return;

  // Ensure positioning context for the dropdown
  const computed = window.getComputedStyle(form);
  if (computed.position === 'static') {
    form.style.position = 'relative';
  }

  // Create a portal wrapper once per form and append to body to escape stacking contexts
  let wrapper = document.querySelector(`[data-header-predictive-wrapper][data-owner="${form.id || ''}"]`);
  if (!wrapper) {
    wrapper = document.createElement('div');
    wrapper.className = 'predictive-search-form__content-wrapper';
    wrapper.setAttribute('data-header-predictive-wrapper', '');
    if (form.id) wrapper.setAttribute('data-owner', form.id);
    wrapper.hidden = true;
    // Inline minimal styles for placement
    wrapper.style.position = 'fixed';
    wrapper.style.left = '0';
    wrapper.style.top = '0';
    wrapper.style.width = '0';
    wrapper.style.transform = 'translate3d(0,0,0)';
    // Always above floating UI like facets
    wrapper.style.zIndex = 'var(--layer-overlay)';
    wrapper.style.background = 'var(--color-background, #fff)';
    wrapper.style.borderRadius = '0 0 var(--search-border-radius, 8px) var(--search-border-radius, 8px)';
    wrapper.style.boxShadow = '0 6px 24px rgba(0,0,0,0.08)';
    const content = document.createElement('div');
    content.className = 'predictive-search-form__content';
    content.setAttribute('data-header-predictive-content', '');
    content.tabIndex = -1;
    content.style.maxHeight = '50vh';
    content.style.overflowY = 'auto';
    wrapper.appendChild(content);
    document.body.appendChild(wrapper);
  }

  const content = wrapper.querySelector('[data-header-predictive-content]');
  if (!content) return;

  const positionWrapper = () => {
    const rect = input.getBoundingClientRect();
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const viewport = window.visualViewport || { width: window.innerWidth, height: window.innerHeight, offsetTop: 0, offsetLeft: 0 };

    if (isMobile) {
      const left = 0 + (viewport.offsetLeft || 0);
      const top = Math.round(rect.bottom + (viewport.offsetTop || 0));
      const width = Math.round(viewport.width);

      wrapper.style.left = `${left}px`;
      wrapper.style.top = `${top}px`;
      wrapper.style.width = `${width}px`;
      wrapper.style.borderRadius = '0';

      const maxH = Math.max(160, Math.floor(viewport.height - (rect.bottom - (viewport.offsetTop || 0)) - 16));
      content.style.maxHeight = `${maxH}px`;
      content.style.paddingBottom = 'calc(var(--padding-lg, 16px) + env(safe-area-inset-bottom, 0px))';
    } else {
      wrapper.style.left = `${Math.round(rect.left)}px`;
      wrapper.style.top = `${Math.round(rect.bottom)}px`;
      wrapper.style.width = `${Math.round(rect.width)}px`;
      wrapper.style.borderRadius = '0 0 var(--search-border-radius, 8px) var(--search-border-radius, 8px)';
      content.style.maxHeight = '50vh';
      content.style.paddingBottom = '';
    }
  };

  const hide = () => {
    wrapper.hidden = true;
    content.innerHTML = '';
  };

  let activeAbort = null;
  const fetchAndRender = async (term) => {
    try {
      if (activeAbort) activeAbort.abort();
      const abortController = new AbortController();
      activeAbort = abortController;

      // Try Section Rendering API first
      const base = Theme?.routes?.search_url || '/search';
      const url = new URL(base, location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('type', 'product');

      // If a collection is selected, pass its id to filter results server-side
      const select = form.querySelector('[name="collection_handle"]');
      if (select && select instanceof HTMLSelectElement && select.value) {
        const option = select.options[select.selectedIndex];
        const collectionId = option && option.dataset ? option.dataset.id : null;
        if (collectionId) {
          url.searchParams.set('collection_id', collectionId);
        }
      }

      let html = null;
      try {
        html = await sectionRenderer.getSectionHTML('predictive-search', false, url);
      } catch (e) {
        // Fallback to view=predictive to support locales/envs that block section rendering by query
        const fallbackUrl = new URL(base, location.origin);
        fallbackUrl.searchParams.set('q', term);
        fallbackUrl.searchParams.set('type', 'product');
        // propagate collection filter
        const collectionId = url.searchParams.get('collection_id');
        if (collectionId) fallbackUrl.searchParams.set('collection_id', collectionId);
        fallbackUrl.searchParams.set('view', 'predictive');
        const res = await fetch(fallbackUrl.toString(), { signal: abortController.signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!res.ok) throw new Error(`Predictive fallback failed: ${res.status}`);
        html = await res.text();
      }
      if (abortController.signal.aborted) return;

      const fragment = new DOMParser().parseFromString(html, 'text/html');
      let results = fragment.getElementById('predictive-search-results');

      if (!results) {
        hide();
        return;
      }

      // Client-side filter by selected collection if needed (search API doesn't support collection param)
      const selectEl = form.querySelector('[name="collection_handle"]');
      const selectedOption = selectEl && selectEl instanceof HTMLSelectElement ? selectEl.options[selectEl.selectedIndex] : null;
      const collectionIdSelected = selectedOption && selectedOption.dataset ? selectedOption.dataset.id : null;

      if (collectionIdSelected) {
        const container = results.querySelector('#predictive-search-products .predictive-search-results__wrapper-products');
        if (container) {
          container.querySelectorAll('.predictive-search-results__card--product').forEach((li) => {
            const ids = li.getAttribute('data-collection-ids') || '';
            const idList = ids.split(',').filter(Boolean);
            if (!idList.includes(collectionIdSelected)) {
              li.remove();
            }
          });
        }
      }

      morph(content, results);
      positionWrapper();
      wrapper.hidden = false;
    } catch (e) {
      hide();
    }
  };

  const onInput = debounce(() => {
    const term = input.value.trim();
    if (!term) {
      hide();
      return;
    }
    fetchAndRender(term);
  }, 200);

  input.addEventListener('input', onInput);

  // 予測検索の再描画がセレクトの選択状態を初期化してしまうため、
  // `collection_handle` の change イベントには反応させない

  form.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') hide();
  });

  document.addEventListener('click', (e) => {
    if (!form.contains(e.target) && !wrapper.contains(e.target)) hide();
  });

  // Close on navigation away
  window.addEventListener('pageshow', hide);

  // Keep positioned on scroll/resize when visible
  const onViewportChange = () => { if (!wrapper.hidden) positionWrapper(); };
  window.addEventListener('scroll', onViewportChange, true);
  window.addEventListener('resize', onViewportChange);
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', onViewportChange);
    window.visualViewport.addEventListener('scroll', onViewportChange);
  }
}

function bindAllHeaderSearchForms(root = document) {
  root.querySelectorAll('.header-search-form').forEach((form) => {
    if (form.hasAttribute('data-predictive-bound')) return;
    form.setAttribute('data-predictive-bound', '');
    initHeaderPredictive(form);
  });
}

function startBinding() {
  bindAllHeaderSearchForms(document);

  // Observe DOM changes to bind forms added later (e.g., section renders)
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches && node.matches('.header-search-form')) {
          bindAllHeaderSearchForms(node.parentElement || document);
        } else {
          bindAllHeaderSearchForms(node);
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // Lazy bind: if an input event occurs on a header search input before binding, bind on-the-fly
  document.addEventListener(
    'input',
    (e) => {
      const target = /** @type {HTMLElement} */ (e.target);
      if (!target) return;
      const input = target.closest?.('.header-search-form__input');
      if (!input) return;
      const form = input.closest?.('.header-search-form');
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.hasAttribute('data-predictive-bound')) {
        form.setAttribute('data-predictive-bound', '');
        initHeaderPredictive(form);
        // retrigger to ensure first input renders suggestions
        input.dispatchEvent(new Event('input'));
      }
    },
    true
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startBinding);
} else {
  startBinding();
}


